import {
  type CustomAudiencesSdk,
  createLiveCustomAudiencesApi
} from 'components/CustomAudiences/api/customAudiencesApi';
import { customAudiencesBaseUrl } from 'components/CustomAudiences/api/request';
import type { CustomAudiencesApiContext } from 'components/CustomAudiences/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useBrandStore } from 'stores';
import useAccessToken from './useAccessToken';
import useProximaSDK from './useProximaSDK';

/* The base URL comes from build-time env, so it is read once at module load */
const SDK_OPTIONS = {
  basePath: customAudiencesBaseUrl() ?? undefined,
  authorization: 'caller',
  trackLoading: false
} as const;

/* proxima-integrations classes take the integrations URL `useProximaSDK` picks by api name */
const INTEGRATIONS_SDK_OPTIONS = { authorization: 'caller', trackLoading: false } as const;

/**
 * The Custom Audiences api context: the brand, a brand-scoped token getter, and the api the store calls.
 *
 * - The api calls the proxima-audiences SDK classes through `useProximaSDK`, at `VITE_AUDIENCES_API_URL`, and `FbIntegrationApi` at `VITE_PROXIMA_INTEGRATIONS_API_URL` for the Meta terms re-check.
 * - Those classes are caller-authorized: `api/request` sends the `Authorization` header on each attempt, so `useProximaSDK` makes no Auth0 call.
 * - They do not track `loading`, since nothing reads it, so calls cause no extra renders.
 * - Tokens come from `useAccessToken`, which also logs out on an unrecoverable session.
 *
 * The returned object changes only when the brand does, so effects can depend on it.
 */
const useCustomAudiencesApi = (): CustomAudiencesApiContext => {
  const definitions = useProximaSDK('AudienceDefinitionsApi', SDK_OPTIONS);
  const snapshots = useProximaSDK('AudienceSnapshotsApi', SDK_OPTIONS);
  const destinations = useProximaSDK('AudienceDestinationsApi', SDK_OPTIONS);
  const deliveries = useProximaSDK('AudienceDeliveriesApi', SDK_OPTIONS);
  const adAccounts = useProximaSDK('AdAccountsApi', SDK_OPTIONS);
  const fbIntegration = useProximaSDK('FbIntegrationApi', INTEGRATIONS_SDK_OPTIONS);
  const { getAccessToken } = useAccessToken();

  /* useProximaSDK and useAccessToken return new objects each render, so calls read the latest through refs */
  const sdk: CustomAudiencesSdk = {
    definitions,
    snapshots,
    destinations,
    deliveries,
    adAccounts,
    fbIntegration
  };
  const sdkRef = useRef(sdk);
  const getAccessTokenRef = useRef(getAccessToken);
  useEffect(() => {
    sdkRef.current = sdk;
    getAccessTokenRef.current = getAccessToken;
  });

  const [api] = useState(() => createLiveCustomAudiencesApi(() => sdkRef.current));
  const brandId = useBrandStore(state => state.brand.brandId);

  return useMemo(
    () => ({
      api,
      brandId,
      getToken: async options =>
        (await getAccessTokenRef.current({ brandId, refresh: options?.refresh })).accessToken
    }),
    [api, brandId]
  );
};

export default useCustomAudiencesApi;
